<template>
  <div data-pill-bar>
    <!-- Scrollable pill buttons -->
    <div class="flex items-start gap-2 overflow-x-auto" style="scrollbar-width: none;">
      <!-- Precio (hasta X + más de X) -->
      <button
        v-if="showPrice"
        type="button"
        data-pill="price"
        @click.stop="$emit('toggle-dropdown', 'price')"
        :class="pillButtonClass(hasPriceFilter, activeDropdown === 'price')"
      >
        {{ priceLabel }}
        <svg :class="['w-3 h-3 transition-transform flex-shrink-0', activeDropdown === 'price' ? 'rotate-180' : '']" fill="none" viewBox="0 0 20 20" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <!-- Estancia mínima (maxMinimumStay = techo del mínimo exigido por el operador) -->
      <button
        type="button"
        data-pill="stay"
        @click.stop="$emit('toggle-dropdown', 'stay')"
        :class="pillButtonClass(!!filters.maxMinimumStay, activeDropdown === 'stay')"
      >
        {{ stayLabel }}
        <svg :class="['w-3 h-3 transition-transform flex-shrink-0', activeDropdown === 'stay' ? 'rotate-180' : '']" fill="none" viewBox="0 0 20 20" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <!-- Estudiantes -->
      <button
        type="button"
        @click="$emit('quick-toggle', 'students')"
        :class="pillButtonClass(filters.forStudents, false)"
      >
        {{ t('colivingSearch.pills.students') }}
      </button>

      <!-- Parejas -->
      <button
        type="button"
        @click="$emit('quick-toggle', 'couples')"
        :class="pillButtonClass(filters.policies.couples, false)"
      >
        {{ t('colivingSearch.pills.couples') }}
      </button>

      <!-- Más filtros -->
      <button
        v-if="!hideMoreFilters"
        type="button"
        @click="$emit('toggle-more')"
        :class="pillButtonClass(moreFiltersActive, showMorePanel)"
      >
        <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
        {{ t('colivingSearch.pills.moreFilters') }}
        <span
          v-if="moreFiltersCount > 0"
          class="ml-0.5 px-1.5 py-0.5 text-[10px] font-bold text-white bg-teal-700 rounded-full min-w-[16px] text-center leading-none"
        >
          {{ moreFiltersCount }}
        </span>
      </button>

      <!-- Limpiar -->
      <template v-if="hasActiveFilters">
        <span class="text-gray-200 text-lg flex-shrink-0 select-none">|</span>
        <button
          type="button"
          @click="$emit('clear')"
          class="flex-shrink-0 px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors whitespace-nowrap"
        >
          {{ t('colivingSearch.pills.clear') }}
        </button>
      </template>
    </div>

    <!-- Dropdown panels — rendered OUTSIDE the overflow container so they're never clipped -->
    <div
      v-if="activeDropdown === 'price' && showPrice"
      class="mt-2 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 max-h-[50vh] overflow-y-auto"
      @click.stop
    >
      <button
        v-for="opt in priceOptions"
        :key="String(opt.value ?? 'none')"
        type="button"
        @click="$emit('select-price', opt.value)"
        :class="['w-full text-left px-4 py-2.5 text-sm transition-colors',
          filters.maxPrice === opt.value ? 'bg-teal-50 text-teal-800 font-semibold' : 'text-gray-700 hover:bg-gray-50']"
      >
        {{ opt.label }}
      </button>
    </div>

    <div
      v-if="activeDropdown === 'stay'"
      class="mt-2 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 max-h-[50vh] overflow-y-auto"
      @click.stop
    >
      <button
        v-for="opt in stayOptions"
        :key="String(opt.value ?? 'none')"
        type="button"
        @click="$emit('select-stay', opt.value)"
        :class="['w-full text-left px-4 py-2.5 text-sm transition-colors',
          filters.maxMinimumStay === opt.value ? 'bg-teal-50 text-teal-800 font-semibold' : 'text-gray-700 hover:bg-gray-50']"
      >
        {{ opt.label }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  filters: { type: Object, required: true },
  showPrice: { type: Boolean, default: false },
  priceOptions: { type: Array, default: () => [] },
  priceLabel: { type: String, default: '' },
  stayOptions: { type: Array, default: () => [] },
  stayLabel: { type: String, default: '' },
  activeDropdown: { type: String, default: null },
  showMorePanel: { type: Boolean, default: false },
  moreFiltersActive: { type: Boolean, default: false },
  moreFiltersCount: { type: Number, default: 0 },
  hasActiveFilters: { type: Boolean, default: false },
  hideMoreFilters: { type: Boolean, default: false },
});

defineEmits(['toggle-dropdown', 'select-price', 'select-stay', 'quick-toggle', 'toggle-more', 'clear']);

const { t } = useI18n();

const hasPriceFilter = computed(() =>
  props.filters.maxPrice != null && props.filters.maxPrice !== 0,
);

function pillButtonClass(isActive, isOpen) {
  return [
    'flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border text-sm font-medium transition-all whitespace-nowrap select-none flex-shrink-0',
    isActive
      ? 'border-teal-600 bg-teal-50 text-teal-800'
      : isOpen
        ? 'border-gray-400 bg-gray-50 text-gray-900'
        : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50',
  ];
}
</script>
